import React, { useState } from 'react';
import { Input, Select, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ExportOutlined, MoreOutlined } from '@ant-design/icons';
import { MasterConfigs } from '../../../../../../../interfaces/entity';
import ReusableTable from '../../../../../../../components/TableComponent';



// 1. Updated Interface based on Figma
interface ActivityRecord {
  _id: string;
  activityType: string;
  sender: {
    name: string;
    code: string;
  };
  contactPerson: {
    name: string;
    designation: string;
  };
  companyName: string;
  subject: string;
  projectName: string;
  dateTime: string;
}

const ActivityLogTable: React.FC = () => {
  const [search, setSearch] = useState('');

  const tableConfig: MasterConfigs = {
    api: () => '/activity-log', // Update your endpoint
    loadPagination: true,
    queryParams: { search },

    // 2. Updated Header with Search and Select Filters
    renderComponent: () => (
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Activity Log</h3>
        <div className="flex gap-2">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined className="text-gray-400" />}
            className="w-64 rounded-md"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select placeholder="Project" className="w-32" allowClear />
          <Select placeholder="Select Employee" className="w-40" allowClear />
          <Select placeholder="Select Type" className="w-32" allowClear />
          <Button icon={<ExportOutlined />} />
        </div>
      </div>
    ),

    // 3. Columns updated to match Figma layout
    getColumns: (): ColumnsType<ActivityRecord> => [
      {
        title: 'Activity Type',
        dataIndex: 'activityType',
        key: 'activityType',
      },
      {
        title: 'Sender/BDE',
        dataIndex: 'sender',
        render: (sender) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold border border-gray-300">
              D
            </div>
            <div>
              <div className="text-xs font-bold">{sender.name}</div>
              <div className="text-[10px] text-gray-400 uppercase">{sender.code}</div>
            </div>
          </div>
        ),
      },
      {
        title: 'Contact Person',
        dataIndex: 'contactPerson',
        render: (contact) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold border border-gray-300">
              S
            </div>
            <div>
              <div className="text-xs font-bold">{contact.name}</div>
              <div className="text-[10px] text-gray-400 uppercase">{contact.designation}</div>
            </div>
          </div>
        ),
      },
      {
        title: 'Company Name',
        dataIndex: 'companyName',
      },
      {
        title: 'Subject',
        dataIndex: 'subject',
        render: (text) => <span className="text-gray-500 italic">{text}</span>,
      },
      {
        title: 'Project Name',
        dataIndex: 'projectName',
        render: (text) => <span className="text-gray-500 italic">{text}</span>,
      },
      {
        title: 'Client Company', // Duplicate column as per Figma image
        dataIndex: 'Clientcompany',
      },
      {
        title: 'Date & Time',
        dataIndex: 'dateTime',
        render: (date) => <span className="whitespace-nowrap">{date}</span>,
      },
      {
        title: 'Action',
        align: 'center',
        render: () => (
          <MoreOutlined className="text-gray-400 cursor-pointer text-lg rotate-90" />
        ),
      },
    ],

    tableProps: {
      bordered: false, 
      size: 'middle',
    },
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6 pb-[50px]">
      <ReusableTable<ActivityRecord> config={tableConfig} />
    </div>
  );
};

export default ActivityLogTable;